import { useEffect, useMemo } from 'react';

import { PreviewHeader } from '@/features/workflows/components/simple/preview-screens';
import { cn } from '@/lib/utils';

import type { SessionDetailDto, SessionStepDto } from '../../schemas/sessionSchemas';
import { formatResultDateTime } from '../../sessionDateTime';
import { extractError } from '../pan/pan.utils';

const STATUS_STYLE: Record<string, string> = {
  Passed: 'bg-[#DCFCE7] text-[#166534]',
  Review: 'bg-[#FEF9C3] text-[#854D0E]',
  Failed: 'bg-[#FEE2E2] text-[#991B1B]',
  Pending: 'bg-[#F3F4F6] text-[#374151]',
};

// Read-only Face Match step detail for the view / review screens. The selfie is
// captured in the live session only, so here we present the stored verification
// outcome — no re-run form. Reports not-ready so the review panel's verify
// button stays disabled for this inspection view.
export function FaceDetail({
  step,
  formId,
  onPendingChange,
  onReadyChange,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
  formId: string;
  onPendingChange: (pending: boolean) => void;
  onReadyChange: (ready: boolean) => void;
}) {
  useEffect(() => {
    onPendingChange(false);
    onReadyChange(false);
  }, [onPendingChange, onReadyChange]);

  const error = extractError(step.resultJson);
  const claims = useMemo(() => extractClaims(step.resultJson), [step.resultJson]);

  return (
    <form id={formId} onSubmit={(e) => e.preventDefault()} className="flex flex-col gap-4">
      <PreviewHeader title="Face Verification" subtitle="AWS Face Match" />

      <div className="flex items-center justify-between rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5">
        <span className="text-sm text-[#6B7280]">Status</span>
        <span
          className={cn(
            'rounded-full px-2.5 py-0.5 text-xs font-semibold',
            STATUS_STYLE[step.status] ?? STATUS_STYLE.Pending,
          )}
        >
          {step.status}
        </span>
      </div>

      {claims.length > 0 && (
        <div className="flex flex-col divide-y divide-[#F3F4F6] rounded-lg border border-[#E5E7EB]">
          {claims.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between px-3 py-2.5">
              <span className="text-sm text-[#6B7280]">{label}</span>
              <span className="text-sm font-medium text-[#111827]">{value}</span>
            </div>
          ))}
        </div>
      )}

      {step.status === 'Pending' && (
        <p className="text-xs leading-snug text-[#9CA3AF]">
          This Face Match step is completed by the applicant in the live session (selfie capture).
        </p>
      )}

      {error && step.status === 'Failed' && <p className="text-xs text-[#BF3636]">{error}</p>}
    </form>
  );
}

// Canonical Face Match Return-Data fields (spec §6.5), in display order. The
// stored result is the NORMALISED, filtered object (FaceMatchResultNormalizer +
// ApplyReturnFieldFilter), so we render exactly the keys the admin left enabled.
const FACE_FIELD_LABELS: Array<{ key: string; label: string }> = [
  { key: 'face_matched', label: 'Face Matched' },
  { key: 'face_match_score', label: 'Face Match Score' },
  { key: 'confidence', label: 'Confidence' },
  { key: 'brightness', label: 'Brightness' },
  { key: 'sharpness', label: 'Sharpness' },
  { key: 'review_needed', label: 'Review Needed' },
  { key: 'capture_attempts', label: 'Capture Attempts' },
  { key: 'verification_status', label: 'Status' },
  { key: 'verified_at', label: 'Verified At' },
  { key: 'method_used', label: 'Method Used' },
];

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  // The API returns face_matched / review_needed as the literal strings "yes"/"no"
  // — surface them capitalised (Yes / No) to match the boolean-style fields.
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === 'yes' || v === 'true') return 'Yes';
    if (v === 'no' || v === 'false') return 'No';
  }
  if (key === 'face_match_score' || key === 'confidence') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : `${n.toFixed(0)}%`;
  }
  if (key === 'verified_at') {
    return formatResultDateTime(value);
  }
  return String(value);
}

function extractClaims(resultJson: string | null): [string, string][] {
  if (!resultJson) return [];
  let parsed: Record<string, unknown> | null = null;
  try {
    const p = JSON.parse(resultJson);
    parsed = p && typeof p === 'object' ? (p as Record<string, unknown>) : null;
  } catch {
    return [];
  }
  if (!parsed) return [];
  const out: [string, string][] = [];
  for (const { key, label } of FACE_FIELD_LABELS) {
    if (key in parsed && parsed[key] !== null && parsed[key] !== '') {
      out.push([label, formatValue(key, parsed[key])]);
    }
  }
  return out;
}
